"use client";

import { useEffect, useRef } from "react";
import {
  HAS_APPLIED_INKSOFT_COUPON_KEY,
  PENDING_DISCOUNT_CODE_KEY,
  getLocalStorageValue,
  removeLocalStorageValue,
  setLocalStorageValue,
} from "@/lib/leadgen/storage";
import {
  INKSOFT_SESSION_TOKEN_UPDATED_EVENT,
  getStoredInksoftSessionToken,
  storeInksoftSessionToken,
} from "@/lib/inksoft/session-token";

function getSessionTokenFromQueryString(): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get("SessionToken");
}

export function useInksoftCouponAutoApply() {
  const isApplyingRef = useRef(false);

  useEffect(() => {
    const sessionTokenFromQuery = getSessionTokenFromQueryString();
    if (sessionTokenFromQuery) {
      storeInksoftSessionToken(sessionTokenFromQuery);
    }
  }, []);

  useEffect(() => {
    const applyCoupon = async () => {
      if (isApplyingRef.current) return;

      const alreadyApplied =
        getLocalStorageValue(HAS_APPLIED_INKSOFT_COUPON_KEY) === "true";
      if (alreadyApplied) return;

      const pendingCode = getLocalStorageValue(PENDING_DISCOUNT_CODE_KEY);
      if (!pendingCode) return;

      const sessionToken =
        getSessionTokenFromQueryString() || getStoredInksoftSessionToken();
      if (!sessionToken) return;

      isApplyingRef.current = true;

      try {
        const response = await fetch("/api/inksoft/cart/apply-coupon", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            couponCode: pendingCode,
            sessionToken,
          }),
        });

        if (response.ok) {
          setLocalStorageValue(HAS_APPLIED_INKSOFT_COUPON_KEY, "true");
          removeLocalStorageValue(PENDING_DISCOUNT_CODE_KEY);
        }
      } catch {
        // Keep this silent so we can retry later when the session token updates.
      } finally {
        isApplyingRef.current = false;
      }
    };

    const handleTokenUpdate = () => {
      void applyCoupon();
    };

    window.addEventListener(
      INKSOFT_SESSION_TOKEN_UPDATED_EVENT,
      handleTokenUpdate as EventListener
    );
    void applyCoupon();

    return () => {
      window.removeEventListener(
        INKSOFT_SESSION_TOKEN_UPDATED_EVENT,
        handleTokenUpdate as EventListener
      );
    };
  }, []);
}
